// Lê o arquivo de conversa exportado pela plataforma de WhatsApp.
//
// O print continua valendo — é o que quem usa WhatsApp pessoal consegue
// entregar. Este arquivo é o caminho melhor para quem atende pela nossa
// plataforma: traz a conversa inteira, com hora real, e a análise deixa de
// depender de a IA acertar pixels.
//
// Aqui NÃO se confere a assinatura: o segredo é do servidor, e trazê-lo para o
// navegador o publicaria no bundle. O que se faz aqui é entender o arquivo e
// mostrar o resumo; quem valida é `api/analisar-abordagem`.

export const FORMATO = "instasolutions.conversa.v1";

/** Reconhece o arquivo sem confiar na extensão. */
export function ehExportDeConversa(obj) {
  return !!obj && obj.formato === FORMATO && Array.isArray(obj.mensagens);
}

export async function lerArquivo(file) {
  try {
    const texto = await file.text();
    const obj = JSON.parse(texto);
    if (!ehExportDeConversa(obj)) return { ok: false, erro: "Este arquivo não é um export de conversa da plataforma." };
    return { ok: true, conversa: obj };
  } catch {
    return { ok: false, erro: "Não consegui ler o arquivo (JSON inválido)." };
  }
}

/** O que a tela mostra depois de anexar, para a pessoa conferir antes de salvar. */
export function resumo(conversa) {
  const r = conversa?.resumo || {};
  const ultima = conversa?.conversa?.ultima_mensagem_em || null;
  const mesclado = !!conversa?.mesclado;
  return {
    contato: conversa?.contato?.nome || "—",
    numero: conversa?.contato?.numero || "",
    atendente: conversa?.conversa?.atendente || "",
    total: r.total || 0,
    daEquipe: r.da_equipe || 0,
    doContato: r.do_contato || 0,
    houveResposta: !!r.houve_resposta,
    respostaEmMinutos: r.resposta_em_minutos ?? null,
    ultima,
    // Serve para preencher a data e a hora do contato sem digitação — e sem
    // depender de a pessoa lembrar quando foi.
    contatoEm: ultima ? paraLocal(ultima) : "",
    // Quantos exports foram juntados neste comprovante (1 = arquivo único).
    mesclado,
    fontes: conversa?.fontes || 1,
    // Arquivo único: assinado se a plataforma carimbou. Mesclado: não dá para
    // reassinar no navegador (o segredo é do servidor), então o consolidado sai
    // SEM assinatura — mas cada parte que entrou pode ter vindo assinada, e é
    // isso que `todasAssinadas` diz.
    assinado: mesclado ? false : !!conversa?.assinatura,
    todasAssinadas: mesclado
      ? (Array.isArray(conversa?.assinaturas) && conversa.assinaturas.length > 0 && conversa.assinaturas.every(Boolean))
      : !!conversa?.assinatura,
  };
}

// ── Juntar mais de um export na mesma conversa ──────────────────────
//
// A plataforma exporta a conversa INTEIRA a cada vez. Fez contato hoje e o
// fornecedor não respondeu; amanhã ele respondeu e a conversa andou — o novo
// export traz as mensagens de hoje DE NOVO, mais as novas. Reanexar sem juntar
// contaria as antigas duas vezes. `mesclar` empilha as mensagens de todos os
// exports, joga fora as repetidas e refaz os totais a partir do conjunto limpo.
//
// A linha é a mesma quando o instante (momento), o lado (de) e o texto batem —
// o `momento` é gerado pelo servidor, então a mesma mensagem exportada duas
// vezes chega idêntica. Sem o momento (dado antigo), cai para data+hora+texto.

const chaveMensagem = (m) =>
  `${m?.momento || `${m?.data || ""} ${m?.hora || ""}`}|${m?.de || ""}|${m?.texto || ""}`;

/** Refaz o bloco `resumo` a partir das mensagens já juntadas e ordenadas. */
export function calcularResumo(mensagens = []) {
  const primeiraEquipe = mensagens.find((m) => m.de === "equipe");
  const respostaAposEquipe = primeiraEquipe
    ? mensagens.find((m) => m.de === "contato" && (m.momento || "") > (primeiraEquipe.momento || ""))
    : null;
  let respostaEmMinutos = null;
  if (primeiraEquipe && respostaAposEquipe) {
    const a = Date.parse(primeiraEquipe.momento);
    const b = Date.parse(respostaAposEquipe.momento);
    if (!Number.isNaN(a) && !Number.isNaN(b)) respostaEmMinutos = Math.round((b - a) / 60000);
  }
  return {
    total: mensagens.length,
    da_equipe: mensagens.filter((m) => m.de === "equipe").length,
    do_contato: mensagens.filter((m) => m.de === "contato").length,
    houve_resposta: mensagens.some((m) => m.de === "contato"),
    resposta_em_minutos: respostaEmMinutos,
  };
}

/**
 * Junta vários exports da MESMA conversa num só, sem repetir mensagem. Devolve
 * um objeto no mesmo formato do export (para a análise ler igual), marcado com
 * `mesclado: true` e o número de `fontes`. Não reassina — ver `resumo`.
 */
export function mesclar(conversas = []) {
  const lista = (conversas || []).filter((c) => ehExportDeConversa(c));
  if (lista.length === 0) return null;
  if (lista.length === 1) return lista[0];

  const vistas = new Set();
  const mensagens = [];
  for (const c of lista) {
    for (const m of (c.mensagens || [])) {
      const k = chaveMensagem(m);
      if (vistas.has(k)) continue;
      vistas.add(k);
      mensagens.push(m);
    }
  }
  // Ordena pelo instante — os exports podem chegar fora de ordem, e a análise e
  // o "respondeu em X min" dependem da sequência real.
  mensagens.sort((a, b) => String(a.momento || "").localeCompare(String(b.momento || "")));

  const ultimo = lista[lista.length - 1];
  const primeiraMsg = mensagens[0]?.momento || null;
  const ultimaMsg = mensagens[mensagens.length - 1]?.momento || null;
  // Total de fontes: um export já mesclado conta as que ele mesmo juntou.
  const fontes = lista.reduce((s, c) => s + (c.fontes || 1), 0);
  const assinaturas = lista.flatMap((c) =>
    Array.isArray(c.assinaturas) ? c.assinaturas : [c.assinatura ?? null]);

  return {
    formato: FORMATO,
    gerado_em: ultimo.gerado_em || null,
    mesclado: true,
    fontes,
    origem: ultimo.origem || null,
    conversa: {
      ...(ultimo.conversa || {}),
      iniciada_em: primeiraMsg,
      ultima_mensagem_em: ultimaMsg,
    },
    contato: ultimo.contato || lista.find((c) => c.contato?.nome)?.contato || null,
    resumo: calcularResumo(mensagens),
    mensagens,
    // Guarda a procedência de cada parte (assinatura de origem), sem `assinatura`
    // no topo — o consolidado não é um arquivo único assinado.
    assinaturas,
  };
}

/** ISO em UTC → o formato que o campo datetime-local aceita, no fuso de quem vê. */
function paraLocal(iso) {
  try {
    const d = new Date(iso);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  } catch { return ""; }
}

/** Frase curta para o histórico e para o card. */
export function descricao(s) {
  const partes = [`${s.total} ${s.total === 1 ? "mensagem" : "mensagens"}`];
  if (s.houveResposta) {
    partes.push(s.respostaEmMinutos != null
      ? `respondeu em ${s.respostaEmMinutos < 60 ? `${s.respostaEmMinutos} min` : `${Math.round(s.respostaEmMinutos / 60)} h`}`
      : "com resposta");
  } else {
    partes.push("sem resposta do contato");
  }
  return partes.join(" · ");
}
